
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/hooks/useAuth';
import { useBoardContext } from '@/contexts/BoardContext';
import { LogOut, Calendar, Kanban, Plus, Settings, LayoutDashboard, StickyNote } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { CreateBoardDialog } from './CreateBoardDialog';
import { BoardRename } from './BoardRename';

const Header = () => {
  const { user, signOut } = useAuth();
  const { boards, currentBoardId, setCurrentBoardId } = useBoardContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [showCreateBoard, setShowCreateBoard] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  useEffect(() => {
    console.log('📋 [HEADER] Current Board ID changed:', currentBoardId);
  }, [currentBoardId]);

  const handleSignOut = async () => {
    console.log('📋 [HEADER] Sign out initiated');
    setSigningOut(true);
    
    try {
      const { error } = await signOut();
      
      if (error) {
        console.error('📋 [HEADER] Sign out error:', error);
      } else {
        console.log('📋 [HEADER] Sign out successful, navigating to auth');
        // Force navigation after sign out
        window.location.href = '/auth';
      }
    } catch (error) {
      console.error('📋 [HEADER] Unexpected sign out error:', error);
    }
    
    setSigningOut(false);
  };

  const handleBoardChange = (value: string) => {
    console.log('📋 [HEADER] Board selection changed to:', value);
    
    // Validate the value before setting it
    if (!value || value.trim() === '') {
      console.log('❌ [HEADER] Rejected empty/null board selection');
      return;
    }
    
    // Ensure the board exists in our boards list
    if (boards && !boards.find(board => board.id === value)) {
      console.log('❌ [HEADER] Rejected unknown board selection:', value);
      return;
    }
    
    console.log('✅ [HEADER] Setting board to:', value);
    setCurrentBoardId(value);
  };

  const currentBoard = boards?.find(board => board.id === currentBoardId);

  return (
    <>
      <header className="bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700 shadow-sm">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              KanbanFlow
            </h1>
            
            {currentBoard && (
              <div className="flex items-center gap-2">
                <span className="text-gray-400 dark:text-slate-500">|</span>
                <BoardRename 
                  boardId={currentBoard.id} 
                  currentTitle={currentBoard.title} 
                />
              </div>
            )}
          </div>

          <div className="flex items-center gap-4">
            {/* Navigation */}
            <div className="hidden lg:flex items-center gap-2">
              <Button
                variant={location.pathname === '/dashboard' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2"
              >
                <LayoutDashboard className="h-4 w-4" />
                <span className="hidden xl:inline">Dashboard</span>
              </Button>
              <Button
                variant={location.pathname === '/' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => navigate('/')}
                className="flex items-center gap-2"
              >
                <Kanban className="h-4 w-4" />
                <span className="hidden xl:inline">Board</span>
              </Button>
              <Button
                variant={location.pathname === '/calendar' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => navigate('/calendar')}
                className="flex items-center gap-2"
              >
                <Calendar className="h-4 w-4" />
                <span className="hidden xl:inline">Calendar</span>
              </Button>
              <Button
                variant={location.pathname === '/notes' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => navigate('/notes')}
                className="flex items-center gap-2"
              >
                <StickyNote className="h-4 w-4" />
                <span className="hidden xl:inline">Notes</span>
              </Button>
              {currentBoard && (
                <Button
                  variant={location.pathname === '/board-settings' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => navigate('/board-settings')}
                  className="flex items-center gap-2"
                >
                  <Settings className="h-4 w-4" />
                  <span className="hidden xl:inline">Settings</span>
                </Button>
              )}
            </div>

            {/* Mobile Navigation Menu */}
            <div className="lg:hidden">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  // Simple mobile navigation - cycle through pages
                  const paths = ['/dashboard', '/', '/calendar', '/notes'];
                  const currentIndex = paths.indexOf(location.pathname);
                  const nextIndex = (currentIndex + 1) % paths.length;
                  navigate(paths[nextIndex]);
                }}
                className="flex items-center gap-2"
              >
                {location.pathname === '/dashboard' && <LayoutDashboard className="h-4 w-4" />}
                {location.pathname === '/' && <Kanban className="h-4 w-4" />}
                {location.pathname === '/calendar' && <Calendar className="h-4 w-4" />}
                {location.pathname === '/notes' && <StickyNote className="h-4 w-4" />}
                {location.pathname === '/board-settings' && <Settings className="h-4 w-4" />}
                <span className="text-sm">
                  {location.pathname === '/dashboard' && 'Dashboard'}
                  {location.pathname === '/' && 'Board'}
                  {location.pathname === '/calendar' && 'Calendar'}
                  {location.pathname === '/notes' && 'Notes'}
                  {location.pathname === '/board-settings' && 'Settings'}
                </span>
              </Button>
            </div>

            <div className="h-6 w-px bg-gray-300 dark:bg-slate-600" />

            {/* Board Selection & Creation */}
            <div className="flex items-center gap-2">
              {boards && boards.length > 1 && (
                <Select value={currentBoardId || ''} onValueChange={handleBoardChange}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Select a board" />
                  </SelectTrigger>
                  <SelectContent>
                    {boards.map((board) => (
                      <SelectItem key={board.id} value={board.id}>
                        {board.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCreateBoard(true)}
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                New Board
              </Button>
            </div>

            <div className="h-6 w-px bg-gray-300 dark:bg-slate-600" />

            {/* User Actions */}
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600 dark:text-slate-400">
                {user?.email}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSignOut}
                disabled={signingOut}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-800 dark:text-slate-400 dark:hover:text-slate-200"
              >
                <LogOut className="h-4 w-4" />
                {signingOut ? 'Signing Out...' : 'Sign Out'}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <CreateBoardDialog
        open={showCreateBoard}
        onOpenChange={setShowCreateBoard}
      />
    </>
  );
};

export default Header;
